import { IShippingMethod, ShippingMethodTypes } from '@monofront/types';
import {
  assign,
  sendParent,
  actions,
  choose,
  send,
} from '@monofront/xstate-v4';
import { CHECKOUT_TRANSITIONS } from '../transitions';
import { SELECT_PICKUP_POINT_TRANSITIONS } from './selectPickupPoint';
import { SELECT_TIME_SLOTS_TRANSITIONS } from './selectTimeSlots';
import { SELECT_SHIPPING_METHOD_TRANSITIONS } from './transitions';
import { SELECT_SHIPPING_METHOD_STATES } from './states';
import {
  IFetchShippingMethodsData,
  IPickupShippingMethod,
  ISelectClickAndCollectMethodEvent,
  ISelectCourierMethodEvent,
  ISelectDaySlotEvent,
  ISelectHomeMethodEvent,
  ISelectPickupMethodEvent,
  ISelectPickupPointEvent,
  ISelectShippingAddressEvent,
  ISelectShippingMethodContext,
  ISendToParentEvent,
  ISetDeliveryInstructionEvent,
  IShippingMethodFormatted,
  SelectShippingMethodSyncCartDoneEvent,
} from './types';
import { isClearShippingMethodSelectionForced } from './guards';

export const onFetchFailure = assign<any, any>({
  methodsList: [],
  noShipment: true,
});

const selectByEvent = {
  methodType: (context, event) => event.methodType ?? context.methodType,
  methodSelectedCode: (_, event) => event.method?.code ?? null,
  shippingMethod: (context, event) => ({
    ...context.shippingMethod,
    shipmentMethodCode:
      event.method?.code ?? context.shippingMethod.shipmentMethodCode,
  }),
};

export const selectHomeMethodByEvent = assign<
  ISelectShippingMethodContext,
  ISelectHomeMethodEvent
>(selectByEvent);

export const selectClickAndCollectMethodByEvent = assign<
  ISelectShippingMethodContext,
  ISelectClickAndCollectMethodEvent
>(selectByEvent);

export const selectCourierMethodByEvent = assign<
  ISelectShippingMethodContext,
  ISelectCourierMethodEvent
>(selectByEvent);

export const selectPickupMethodByEvent = assign<
  ISelectShippingMethodContext,
  ISelectPickupMethodEvent
>(selectByEvent);

export const resetMethodSelected = assign<any, any>({
  methodSelected: null,
});

export const selectPickupMethodEvent = send<
  ISelectShippingMethodContext,
  ISelectPickupMethodEvent
>(
  (_, event) => ({
    type: SELECT_PICKUP_POINT_TRANSITIONS.OPEN,
    address: event.address,
    methods: event.method.methods,
  }),
  {
    to: (context) => context.selectPickupPointRef!,
  },
);

export const selectPickupPointEvent = assign<
  ISelectShippingMethodContext,
  ISelectPickupPointEvent
>({
  shippingMethod: (_, event) => ({
    shipmentMethodCode: event.shipmentMethodCode,
    pointId: event.pickupPointSelected.pointId,
    address: event.pickupAddress,
  }),
});

export const sendFetchToTimeSlotAction = send<
  ISelectShippingMethodContext,
  ISelectCourierMethodEvent
>(
  (context, event) => {
    return {
      type: SELECT_TIME_SLOTS_TRANSITIONS.FETCH_SLOTS,
      checkoutCartId: event?.checkoutCartId || context.checkoutCartId,
      carrierMethod:
        event?.method?.tmsCode ||
        (
          context.methodsList.find(
            (method) => method.code === context.methodSelectedCode,
          ) as IShippingMethod
        )?.tmsCode,
      isFresh: context.isFreshCart,
      isUltraFresh: context.isUltraFreshCart,
    };
  },

  {
    to: (context) => context.selectTimeSlotsRef!,
  },
);

export const sendClearTimeSlotsSelectionAction = send<
  ISelectShippingMethodContext,
  any
>(
  () => ({
    type: SELECT_TIME_SLOTS_TRANSITIONS.CLEAR_SELECTION,
  }),
  {
    to: (context) => context.selectTimeSlotsRef!,
  },
);

export const selectTimeSlotHourEvent = assign<
  ISelectShippingMethodContext,
  ISelectDaySlotEvent
>({
  shippingMethod: (context, event) => ({
    ...context.shippingMethod,
    ...event.timeSlotSelected,
  }),
});

export const assignDeliveryInfosFromShippingAddress = assign<
  ISelectShippingMethodContext,
  ISelectShippingAddressEvent
>({
  shippingMethod: (context, event) => ({
    ...context.shippingMethod,
    deliveryInstruction: event.shippingAddressSelected.deliveryInstruction,
  }),
});

export const assignSetDeliveryInfos = assign<
  ISelectShippingMethodContext,
  ISetDeliveryInstructionEvent
>({
  shippingMethod: (context, event) => ({
    ...context.shippingMethod,
    deliveryInstruction: event.deliveryInstruction,
  }),
});

export const sendShippingMethodSelected = sendParent<
  ISelectShippingMethodContext,
  ISendToParentEvent | any
>((context, event) => ({
  type: CHECKOUT_TRANSITIONS.SELECT_SHIPPING_METHOD,
  methodType: event.methodType ?? context.methodType,
  shippingMethod: context.shippingMethod,
}));

export const cancelSendToParent = actions.cancel('sendToParentDelayed');

export const sendPickupToParent = actions.send<
  ISelectShippingMethodContext,
  ISelectPickupPointEvent
>(
  {
    type: SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT,
    methodType: ShippingMethodTypes.PICKUP,
  },
  { delay: 1000, id: 'sendToParentDelayed' },
);

export const sendCourierToParent = actions.send<
  ISelectShippingMethodContext,
  ISelectDaySlotEvent | ISetDeliveryInstructionEvent
>(
  {
    type: SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT,
    methodType: ShippingMethodTypes.COURIER,
  },
  { delay: 1000, id: 'sendToParentDelayed' },
);

export const sendHomeToParent = actions.send<ISelectShippingMethodContext, any>(
  {
    type: SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT,
    methodType: ShippingMethodTypes.HOME,
  },
  { delay: 1000, id: 'sendToParentDelayed' },
);

export const sendClickAndCollectToParent = actions.send<
  ISelectShippingMethodContext,
  ISelectClickAndCollectMethodEvent
>(
  {
    type: SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT,
    methodType: ShippingMethodTypes.CLICK_AND_COLLECT,
  },
  { delay: 1000, id: 'sendToParentDelayed' },
);

export const assignCourierMethod = assign<
  ISelectShippingMethodContext,
  ISelectDaySlotEvent
>({
  shippingMethod: (context, event) => ({
    ...context.shippingMethod,
    ...event.timeSlotSelected,
    deliveryInstruction: context.shippingMethod.deliveryInstruction,
  }),
});

export const assignShippingMethodsReceived = assign<
  ISelectShippingMethodContext,
  { type: any; data: IFetchShippingMethodsData }
>({
  methodsList: (_, event) =>
    event?.data?.data?.listShippingMethods?.reduce((acc, method) => {
      if (method.type === ShippingMethodTypes.PICKUP) {
        const pickupEl = acc.find(
          ({ type }) => type === ShippingMethodTypes.PICKUP,
        );

        if (!pickupEl) {
          acc.push({
            type: ShippingMethodTypes.PICKUP,
            methods: [method],
            code: ShippingMethodTypes.PICKUP,
          });
        } else {
          (pickupEl as IPickupShippingMethod).methods.push(method);
        }
      } else {
        acc.push(method);
      }

      return acc;
    }, [] as IShippingMethodFormatted[]) ?? [],
  noShipment: (_, event) =>
    !event.data?.data?.listShippingMethods ||
    event.data.data.listShippingMethods.length === 0,
});

export const assignFirstShippingMethodReceived = assign<
  ISelectShippingMethodContext,
  any
>({
  methodType: (context) =>
    (context.methodsList[0]?.type as ShippingMethodTypes) ?? null,
  methodSelectedCode: (context) => context.methodsList[0]?.code ?? null,
  shippingMethod: (context) => ({
    ...context.shippingMethod,
    shipmentMethodCode: context.methodsList[0]?.code ?? null,
  }),
});

export const assignClearShippingMethod = assign<
  ISelectShippingMethodContext,
  any
>({
  methodSelectedCode: null,
  methodType: null,
  shippingMethod: {
    shipmentMethodCode: null,
    deliveryDate: undefined,
    timeSlot: undefined,
    deliveryInstruction: undefined,
    pointId: undefined,
    address: undefined,
  },
});

export enum SELECT_SHIPPING_METHOD_EXTERNAL_ACTIONS {
  spawnMachines = 'spawnMachines',
  fetchShippingMethodsError = 'fetchShippingMethodsError',
}

export const selectPickupPointTransition = {
  actions: [selectPickupPointEvent, cancelSendToParent, sendPickupToParent],
  target: SELECT_SHIPPING_METHOD_STATES.PICKUP_POINT_SELECTED,
};

export const selectDaySlotTransition = {
  target: SELECT_SHIPPING_METHOD_STATES.TIME_SLOT_SELECTED,
  actions: [selectTimeSlotHourEvent, cancelSendToParent, sendCourierToParent],
};

export const selectHomeMethodTransition = {
  target: SELECT_SHIPPING_METHOD_STATES.HOME_METHOD_SELECTED,
  actions: [selectHomeMethodByEvent, cancelSendToParent, sendHomeToParent],
};

export const selectClickAndCollectMethodTransition = {
  target: SELECT_SHIPPING_METHOD_STATES.CLICK_AND_COLLECT_SELECTED,
  actions: [
    selectClickAndCollectMethodByEvent,
    cancelSendToParent,
    sendClickAndCollectToParent,
  ],
};

export const selectPickupMethodTransition = {
  target: SELECT_SHIPPING_METHOD_STATES.PICKUP_METHOD_SELECTED,
  actions: [
    selectPickupMethodEvent,
    resetMethodSelected,
    selectPickupMethodByEvent,
  ],
};

export const selectCourierMethodTransition = {
  target: SELECT_SHIPPING_METHOD_STATES.COURIER_METHOD_SELECTED,
  actions: [selectCourierMethodByEvent, sendFetchToTimeSlotAction],
};

export const fetchShippingMethods = {
  target: SELECT_SHIPPING_METHOD_STATES.METHODS_FETCHING,
  actions: [
    choose([
      {
        actions: [sendClearTimeSlotsSelectionAction, assignClearShippingMethod],
        cond: isClearShippingMethodSelectionForced,
      },
    ]),
  ],
};

export const selectShippingAddressTransition = {
  actions: [
    sendClearTimeSlotsSelectionAction,
    assignClearShippingMethod,
    assignDeliveryInfosFromShippingAddress,
  ],
};

export const assignCheckoutCartId = assign<
  ISelectShippingMethodContext,
  SelectShippingMethodSyncCartDoneEvent
>({
  checkoutCartId: (_, event) => {
    return event.checkoutCartId;
  },
});
